import React, { forwardRef, KeyboardEventHandler, useCallback, useEffect, useRef, useState } from 'react';
import { Input } from '@components/input/src';
import { Item } from '@components/input-time/src/item/item';
import { timeOptions } from '@components/input-time/src/utils/time-options';
import { MD_VIEWPORT } from '@constants/viewport';
import { useOnClickOutside } from '@hooks/use-click-outside';
import { useWindowSize } from '@hooks/use-window-size';
import classNames from 'classnames';

import { Props } from './input-time.type';
import { getTimeFormatted } from './utils';

export const InputTime = forwardRef(
  (
    { id, value, hasError, onChange, children, loadingText, readOnly, options = timeOptions, openUp, ...props }: Props,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ref
  ) => {
    const isMobileViewPort = useWindowSize(MD_VIEWPORT as number);
    const itemsEls = useRef<HTMLLIElement[]>([]);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [cursor, setCursor] = useState<number>(0);

    const handleClickOutside = useCallback(() => {
      setIsOpen(false);
    }, []);

    useOnClickOutside(wrapperRef, handleClickOutside);

    const setInputFocused = useCallback(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, []);

    const handleClickInside = useCallback(() => {
      if (!readOnly) {
        setIsOpen(true);
        setCursor(0);
        setInputFocused();
      }
    }, [setInputFocused, readOnly]);

    const handleClick = (val: string) => {
      onChange?.(val);
      setIsOpen(false);
      setCursor(0);
      setInputFocused();
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const formattedValue = getTimeFormatted(value, e.target.value);
      setIsOpen(true);
      onChange?.(formattedValue);
    };

    const moveToCursor = useCallback(() => {
      if (itemsEls.current[cursor]) {
        itemsEls.current[cursor].scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'end',
        });
      }
    }, [cursor]);

    const handleMouseEnter = useCallback((index: number) => setCursor(index), [setCursor]);
    const handleMouseLeave = useCallback(() => setCursor(0), [setCursor]);

    useEffect(() => {
      if (isOpen) {
        moveToCursor();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const handleArrowDown = () => {
      if (options.length) {
        setCursor((prevState) => (prevState < options.length - 1 ? prevState + 1 : prevState));
      }
    };

    const handleArrowUp = () => {
      if (options.length) {
        setCursor((prevState) => (prevState > 0 ? prevState - 1 : prevState));
      }
    };

    const handleEnter = () => {
      if (!!options.length && isOpen && !isMobileViewPort && value == '') {
        onChange?.(options[cursor]);
      }
    };

    const handleKey: KeyboardEventHandler = (event) => {
      event.stopPropagation();
      const { key } = event;

      // eslint-disable-next-line default-case
      switch (key) {
        case 'ArrowDown': {
          handleArrowDown();
          return;
        }
        case 'ArrowUp': {
          handleArrowUp();
          return;
        }
        case 'Esc': {
          handleClickOutside();
          return;
        }
        case 'Enter': {
          handleEnter();
          event.preventDefault();
        }
      }
    };

    return (
      <div className='relative' ref={wrapperRef}>
        <div aria-haspopup='listbox' aria-expanded={isOpen} aria-labelledby={`${id}-button ${id}`} id={`${id}-button`}>
          <Input
            {...props}
            id={id}
            onClick={handleClickInside}
            value={value}
            onChange={handleChange}
            style={{ minWidth: '70px' }}
            type='text'
            ref={inputRef}
            readOnly={readOnly}
            onKeyDown={readOnly ? undefined : handleKey}
          />
        </div>

        <ul
          id={`${id}-list`}
          className={classNames(
            'absolute z-30 mt-1 min-w-fit w-full bg-primary shadow-sm max-h-56 rounded-md overflow-auto focus:outline-hidden scrollbar-hide',
            {
              hidden: !isOpen || (!options.length && !value) || isMobileViewPort,
              'bottom-16 left-0': openUp,
            }
          )}
          tabIndex={-1}
          role='listbox'
          aria-label={id}
          aria-labelledby={id}
          aria-activedescendant={id}>
          {!!options.length &&
            options.map((option, index) => (
              <Item
                selected={value === option}
                ref={(element: HTMLLIElement) => (itemsEls.current[index] = element)}
                onClick={handleClick}
                key={option}
                onMouseEnter={() => handleMouseEnter(index)}
                onMouseLeave={handleMouseLeave}
                isHovered={cursor === index}
                value={option}
              />
            ))}

          {value && children ? children : null}
        </ul>
      </div>
    );
  }
);
